import React, {useCallback, useEffect, useState} from "react"
import {noop} from "rxjs";
import clsx from "clsx";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

type RadioSelectOption<T> = {
    label: string
    value: T,
    disabled?: boolean
}

type RadioSelectProps<T> = {
    options?: RadioSelectOption<T>[]
    onChange?: (value: T|undefined) => void
    required?: boolean
    value?: T
}

export function RadioSelect<T>({
                                   options = [],
                                   value = undefined,
                                   onChange = noop,
                                   required = false,
                               }: RadioSelectProps<T>) {

    const initialSelectedIndex = (() => {
        if (value !== undefined) {
            const index = options.findIndex(opt => opt.value === value);
            return index >= 0 ? index : (required ? 0 : undefined);
        }
        return required ? 0 : undefined;
    })();

    const [selected, setSelected] = useState<number | undefined>(initialSelectedIndex);





    const isSelected = useCallback((idx: number) => {
        return selected === idx
    }, [selected])


    const isDisabled = useCallback((idx: number) => {
        return options[idx]?.disabled ?? false
    }, [selected])


    const radioStyle = useCallback((idx: number) => {
        return clsx(
            "px-4 py-2 rounded-md bg-white shadow-md text-left flex gap-4 w-[400px] w-full",
            {
                "outline outline-light-grey outline-1": !isSelected(idx),
                "outline outline-blue outline-2": isSelected(idx),
                "opacity-30": isDisabled(idx),
            }
        )
    }, [isSelected, selected])

    const handleSelect = useCallback((idx: number) => {
        return () => {
            if(idx === selected && !required) {
                setSelected(undefined)
                return
            }
            setSelected(idx)
        }
    }, [onChange, selected])

    useEffect(() => {
        if(selected == undefined){
            onChange(undefined)
            return
        }
        onChange(options[selected].value)
    }, [selected])


    const RadioIcon = useCallback(({index}: { index: number }) => {
        if (isSelected(index)) return <RadioButtonCheckedIcon className={"text-blue"}/>
        return <RadioButtonUncheckedIcon/>
    }, [selected])


    return (
        <div className="flex flex-col gap-4">
            {options.map((option, i) => (
                <div key={i} className="w-full">
                    <button className={radioStyle(i)}
                            onClick={handleSelect(i)}
                            disabled={isDisabled(i)}>

                        <div className="flex-grow">
                            {option.label}
                        </div>
                        <div>
                            <RadioIcon index={i}/>
                        </div>
                    </button>
                </div>
            ))}
        </div>

    )


}